import { isNil } from 'lodash';
import { createContext, useCallback, useEffect } from 'react';
import { InjectedRouter, PlainRoute } from 'react-router';
import { Config } from '@shared/config';
import { RETURN_CHANNEL_SLUG_QUERY } from '@shared/scenes/cards/form/update/constants';
import { i18n } from '@shared/util/i18n';
import { RouteName } from './routeParams';
import {
  Location,
  useNavigationBeforeLeave,
  useNavigationFocus,
  useNavigator,
} from './useNavigator';

interface ReactRouterProps {
  routes: PlainRoute[];
  params: Record<string, any>;
  router?: InjectedRouter;
}

export const ReactRouterContext = createContext<ReactRouterProps>({
  routes: [],
  params: {},
});

export function useUnsavedChangesNavigationWarning(enabled: boolean) {
  const focused = useNavigationFocus();
  const callback = useCallback(
    (nextLocation: Location): boolean | Promise<boolean> => {
      const isSubformRedirect = !isNil(nextLocation?.params?.[RETURN_CHANNEL_SLUG_QUERY]);

      if (isSubformRedirect || !enabled || !focused) {
        return true;
      }

      // return false to prevent navigation. can't use MBAlert here because this
      // callback needs to be synchronous

      // eslint-disable-next-line no-alert
      return window.confirm(i18n('unsavedChangesWarning.web.message'));
    },
    [enabled, focused],
  );

  useNavigationBeforeLeave(callback);

  // Add beforeunload handler to catch browser navigation (window.location, tab close, refresh, etc.)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Modern browsers ignore the custom message and show their own
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);
}

export function useNavigateToRoute(routeName: RouteName): VoidFunction {
  const navigator = useNavigator();
  return useCallback(() => navigator.navigate(routeName), [navigator, routeName]);
}

interface BuilderLinkProps {
  route: string;
  isNested?: boolean;
  includeOverlay?: boolean;
  includeMenu?: boolean;
}

/**
 * Hook for navigating to the builder app with proper history management.
 *
 * @param workflowTemplateId - The workflow template ID to navigate to in builder
 * @returns A callback function that accepts props and navigates to builder
 *
 * @example
 * const navigateToBuilder = useBuilderLink(workflowTemplateId);
 * navigateToBuilder({ route: '/field' }); // Navigates to builder fields page
 * navigateToBuilder({ route: '/subform', isNested: true }); // Navigates to builder subforms page
 * navigateToBuilder({ route: '/template/e', isNested: true }); // Navigates to builder view templates page
 * navigateToBuilder({ route: '/automation', includeOverlay: true }); // Navigates to builder automations page with overlay
 */
export function useBuilderLink(workflowTemplateId?: string) {
  return useCallback(
    ({
      route,
      isNested = false,
      includeOverlay = false,
      includeMenu = !isNested,
    }: BuilderLinkProps) => {
      if (!workflowTemplateId) {
        console.error('useBuilderLink: workflowTemplateId is required');
        return;
      }
      const currentUrl = window.location.href;

      // Push current URL to history before navigating away
      window.history.pushState(null, '', currentUrl);

      // Ensure route starts with /
      const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
      const queryParams = new URLSearchParams();
      queryParams.set('returnTo', currentUrl);
      queryParams.set('nested', String(isNested));
      queryParams.set('overlay', String(includeOverlay));
      queryParams.set('menu', String(includeMenu));

      // Navigate to builder with query parameters
      window.location.href = `${
        Config.BUILDER_APP_URL
      }/${workflowTemplateId}${normalizedRoute}?${queryParams.toString()}`;
    },
    [workflowTemplateId],
  );
}
